import { DriverActionFlow } from '../constants/order-status.constants';

/**
 * Truck statuses in the order the driver actions move through them. This is
 * the same list the admin "Change truck status" modal shows, so every role's
 * execution step is derived from it.
 */
export const EXECUTION_STATUSES: string[] = [
	...DriverActionFlow.map((step) => step.status),
	'completed',
];

/** Order states before the first driver action, followed by the truck statuses. */
export const PROGRESS_SEQUENCE: string[] = [
	'to_do',
	'awaiting_confirmation',
	'transporter_confirmed',
	'truck_confirmed',
	...EXECUTION_STATUSES,
];

export const progressRank = (status?: string): number =>
	status ? PROGRESS_SEQUENCE.indexOf(status) : -1;

/**
 * Workaround for the driver app (seen 2026-10-27, orders 395/396/397/398):
 * right after "start-trip-to-loading" it also sends "arrived-to-loading"
 * while its screen still says the driver is heading to the loading place,
 * and from then on every tap sends the action after the one it displays.
 * The platform therefore shows each truck status from arrived_to_loading up
 * to unloaded as the previous one, so it matches what the driver sees.
 * "completed" is left alone so orders still reach the payment steps.
 * Delete this once the app only sends the action it displays.
 */
const SHIFT_FROM = 'arrived_to_loading';
const SHIFT_TO = 'unloaded';

const DISPLAYED_TRUCK_STATUS: Record<string, string> = EXECUTION_STATUSES.slice(
	EXECUTION_STATUSES.indexOf(SHIFT_FROM),
	EXECUTION_STATUSES.indexOf(SHIFT_TO) + 1,
).reduce<Record<string, string>>((map, status) => {
	map[status] = EXECUTION_STATUSES[EXECUTION_STATUSES.indexOf(status) - 1];
	return map;
}, {});

/** The truck status the platform shows for a raw truck status. */
export const displayedTruckStatus = (status?: string): string | undefined =>
	(status && DISPLAYED_TRUCK_STATUS[status]) || status;

interface ExecutionSource {
	state?: string;
	deliveryIncluded?: boolean;
	trucks?: Array<{ orderStatus?: string } | null | undefined> | null;
}

/**
 * Driver actions (from the driver app or the admin modal) move the truck
 * status, while the per-role order state is derived from it afterwards and
 * can lag or skip steps. Reading the truck status keeps every role on the
 * same step the modal shows; the order state only matters before a truck is
 * sent to a driver.
 */
export const resolveExecutionStatus = (
	order: ExecutionSource,
): string | undefined => {
	const truckStatus =
		order.deliveryIncluded && order.trucks?.length === 1
			? order.trucks[0]?.orderStatus
			: undefined;

	return truckStatus && EXECUTION_STATUSES.includes(truckStatus)
		? displayedTruckStatus(truckStatus)
		: order.state;
};
